import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Lock, ShieldCheck, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  fetchAgreementStatus, fetchNdaDocument, signAgreement,
  type NdaDocument as NdaDocumentPayload,
} from '../../api/client';
import { useAuditStore } from '../../stores/useAuditStore';
import { useAuthStore } from '../../stores/useAuthStore';
import LegalDialog, { type LegalDocument } from '../legal/LegalDialog';
import NdaDocument from './NdaDocument';
import SignaturePad, { type SignatureKind } from './SignaturePad';
import OnboardingFooter from './OnboardingFooter';
import './onboarding.css';

/**
 * Optional agreement screen — review and sign the mutual NDA.
 *
 * The document fills in as the signer types, so what they agree to is visibly
 * theirs rather than a generic wall of text. Nothing is recorded until all
 * three consents are ticked; the backend re-renders and hashes the same
 * document at submit time, so the stored record is independent of this page.
 */

const CONSENTS = [
  {
    id: 'esign' as const,
    body: (
      <>
        I consent to sign electronically. My electronic signature is{' '}
        <strong className="font-semibold text-foreground">legally binding (ESIGN / UETA)</strong>,
        equivalent to a handwritten signature.
      </>
    ),
  },
  { id: 'privacy' as const, doc: 'privacy' as const, label: 'Privacy Policy' },
  { id: 'terms' as const, doc: 'terms' as const, label: 'Terms of Service' },
];

const ASSURANCES = [
  { Icon: Eye, text: 'Read-only access to the platforms you connect — we never post, edit or spend.' },
  { Icon: Lock, text: 'Your data is used only to produce your audit, never to train models or share with third parties.' },
  { Icon: ShieldCheck, text: 'Mutual NDA covers anything you upload and anything we report back.' },
  { Icon: Trash2, text: 'Disconnect any source at any time — we delete it within 24 hours.' },
];

type ConsentState = { esign: boolean; privacy: boolean; terms: boolean };

export default function SignView() {
  const navigate = useNavigate();
  const authEmail = useAuthStore((s) => s.user?.email ?? '');
  const projectName = useAuditStore((s) => s.projectName);

  const [doc, setDoc] = useState<NdaDocumentPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [company, setCompany] = useState(projectName);
  const [signerName, setSignerName] = useState('');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<SignatureKind>('drawn');
  const [image, setImage] = useState<string | null>(null);
  const [consents, setConsents] = useState<ConsentState>({
    esign: false, privacy: false, terms: false,
  });
  const [legalDoc, setLegalDoc] = useState<LegalDocument | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const checkedRef = useRef(false);

  // Already signed? Straight on to Connect — signing once is enough, and the
  // signed PDF is reached from the back office rather than from this flow.
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    fetchAgreementStatus()
      .then((status) => {
        if (status.signed) navigate('/connect', { replace: true });
      })
      .catch(() => { /* an unreachable status check shouldn't block signing */ });
  }, [navigate]);

  useEffect(() => {
    fetchNdaDocument()
      .then(setDoc)
      .catch(() => setLoadError("The agreement couldn't be loaded. Reload the page to try again."));
  }, []);

  const mergeValues = useMemo(
    () => ({
      date: doc?.date ?? '',
      company,
      signer_name: signerName,
      signer_title: title,
      signer_email: authEmail,
    }),
    [doc?.date, company, signerName, title, authEmail],
  );

  const ready =
    Boolean(doc) &&
    company.trim().length > 0 &&
    signerName.trim().length > 1 &&
    // Both remaining modes produce an image, so a signature is never optional.
    Boolean(image) &&
    consents.esign && consents.privacy && consents.terms;

  const submit = useCallback(async () => {
    // `image` is also checked by `ready`; repeating it here narrows the type
    // and keeps the guard true even if `ready` is ever relaxed.
    if (!ready || submitting || !image) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await signAgreement({
        company: company.trim(),
        signer_name: signerName.trim(),
        signer_title: title.trim(),
        signer_email: authEmail,
        signature_kind: kind,
        signature_data: image,
        consents,
      });
      navigate('/connect');
    } catch {
      setSubmitError("We couldn't record your signature. Check your connection and try again.");
      setSubmitting(false);
    }
  }, [ready, submitting, company, signerName, title, authEmail, kind, image, consents, navigate]);

  return (
    <>
      <LegalDialog doc={legalDoc} onClose={() => setLegalDoc(null)} />

      <div className="mx-auto max-w-[1180px] px-4 pb-24 pt-8 sm:px-8">
        <h1 className="mb-1 text-xl font-bold tracking-tight text-foreground">Review &amp; sign</h1>
        <p className="mb-5 text-sm text-muted-foreground">
          Two documents before we touch your data. Signing takes a few seconds and is legally binding.
        </p>

        <div className="grid items-start gap-11 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="mb-5 grid gap-2.5 sm:grid-cols-2 sm:gap-x-4">
              <Field id="sign-company" label="Company">
                <Input
                  id="sign-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Legal entity name…"
                />
              </Field>
              <Field id="sign-name" label="Signer name">
                <Input
                  id="sign-name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Full name…"
                />
              </Field>
              <Field id="sign-title" label="Title">
                <Input
                  id="sign-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. CEO"
                />
              </Field>
              <Field id="sign-email" label="Email" note="from your account">
                <div className="relative">
                  <Input id="sign-email" value={authEmail} readOnly className="bg-muted pr-9 text-muted-foreground" />
                  <Lock className="pointer-events-none absolute right-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                </div>
              </Field>
            </div>

            {loadError ? (
              <p className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {loadError}
              </p>
            ) : (
              <NdaDocument
                sections={doc?.template.sections ?? []}
                values={mergeValues}
                className="max-h-[46vh] min-h-[160px]"
              />
            )}

            <div className="mt-5">
              <SignaturePad
                kind={kind}
                onKindChange={setKind}
                onImageChange={setImage}
              />
            </div>

            <div className="mt-3.5 space-y-2.5">
              {CONSENTS.map((consent) => (
                <label
                  key={consent.id}
                  className="flex cursor-pointer items-start gap-3 text-[12.5px] leading-relaxed text-muted-foreground"
                >
                  <Checkbox
                    checked={consents[consent.id]}
                    onChange={(next) => setConsents((prev) => ({ ...prev, [consent.id]: next }))}
                  />
                  <span>
                    {consent.body ?? (
                      <>
                        I have read and agree to the{' '}
                        <button
                          type="button"
                          onClick={(e) => {
                            // Don't let the label toggle the box — the user is
                            // asking to read, not to agree.
                            e.preventDefault();
                            e.stopPropagation();
                            setLegalDoc(consent.doc!);
                          }}
                          className="cursor-pointer font-semibold text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
                        >
                          {consent.label}
                        </button>
                        .
                      </>
                    )}
                  </span>
                </label>
              ))}
            </div>

            <p className="mt-3.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="size-3.5 shrink-0" />
              Encrypted · audit trail recorded (identity, time, IP, document hash)
            </p>

            {submitError && (
              <p className="mt-3 text-sm text-destructive">{submitError}</p>
            )}
          </div>

          <aside>
            <p className="mb-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
              What you're agreeing to
            </p>
            <ul className="flex flex-col gap-3 text-[12.5px] leading-relaxed text-muted-foreground">
              {ASSURANCES.map(({ Icon, text }) => (
                <li key={text} className="flex items-start gap-2.5">
                  <Icon className="mt-0.5 size-4 shrink-0 text-foreground" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>

      <OnboardingFooter
        step={1}
        continueLabel={submitting ? 'Signing…' : 'Agree & sign'}
        continueDisabled={!ready || submitting}
        onContinue={submit}
      />
    </>
  );
}

function Field({
  id, label, note, children,
}: {
  id: string;
  label: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold">
        {label}
        {note && <span className="font-normal text-muted-foreground">&nbsp;{note}</span>}
      </Label>
      {children}
    </div>
  );
}

/** A checkbox that matches the flow's density. shadcn has no checkbox here. */
function Checkbox({
  checked, onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className={cn(
        'mt-0.5 size-[17px] shrink-0 cursor-pointer appearance-none rounded-[5px] border-[1.5px] transition-colors',
        'relative after:absolute after:left-1/2 after:top-[47%] after:h-[9px] after:w-[5px]',
        'after:-translate-x-1/2 after:-translate-y-[55%] after:rotate-[42deg] after:border-[2px]',
        'after:border-l-0 after:border-t-0 after:border-primary-foreground after:content-[""] after:opacity-0',
        checked
          ? 'border-primary bg-primary after:opacity-100'
          : 'border-border bg-card hover:border-muted-foreground',
      )}
    />
  );
}
